import { PriceUtils } from "@repo/common"
import { cn } from "@repo/ui/lib/utils"
import * as React from "react"
import { useTranslations } from "use-intl"

interface BudgetIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  budget: number
  totalCost: number
  showValue?: boolean
}

const BudgetIndicator = React.forwardRef<HTMLDivElement, BudgetIndicatorProps>(
  ({ className, budget, totalCost, showValue = true, ...props }, ref) => {
    const t = useTranslations("ui.budgetIndicator")
    const isOverBudget = totalCost > budget

    // When over budget, show how much of the budget we've used (budget/totalCost)
    // When within budget, show how much of the budget we've used (totalCost/budget)
    const percentage = isOverBudget
      ? Math.min(Math.max((budget / totalCost) * 100, 0), 100)
      : Math.min(Math.max((totalCost / budget) * 100, 0), 100)

    // Determine color based on budget status
    const color = isOverBudget ? "destructive" : "success"

    const colorClasses = {
      success: "bg-success",
      destructive: "bg-destructive",
    }

    const backgroundColorClasses = {
      success: "bg-success-border",
      destructive: "bg-destructive-border",
    }

    const borderColorClasses = {
      success: "border-success",
      destructive: "border-destructive",
    }

    return (
      <div className={cn("w-full space-y-2", className)} {...props}>
        <div
          ref={ref}
          className={cn(
            "relative h-6 w-full overflow-hidden rounded-full border",
            backgroundColorClasses[color],
            borderColorClasses[color],
          )}
        >
          {totalCost === 0 ? (
            <div className="h-full w-full flex items-center justify-center">
              {showValue && (
                <span className="text-xs font-bold text-muted-foreground">
                  {t("noCost")}
                </span>
              )}
            </div>
          ) : (
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300 ease-out flex items-center justify-end pr-3",
                colorClasses[color],
              )}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            >
              {showValue && (
                <span className="text-xs font-bold text-white">
                  {isOverBudget
                    ? PriceUtils.format(budget)
                    : PriceUtils.format(totalCost)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    )
  },
)
BudgetIndicator.displayName = "BudgetIndicator"

export { BudgetIndicator, type BudgetIndicatorProps }
